<template>
  <TresGroup 
    ref="cardGroup"
    :position="animatedPosition" 
    :rotation="animatedRotation" 
    :scale="animatedScale"
    @click="handleClick"
  >
    <!-- Card Background with Image Texture -->
    <TresMesh ref="cardMesh">
      <TresBoxGeometry :args="[2.5, 1.5, 0.1]" />
      <TresMeshStandardMaterial 
        v-if="textureLoaded && project.image"
        :map="cardTexture"
        :transparent="true"
        :opacity="animatedOpacity"
        :metalness="0.1"
        :roughness="0.3"
      />
      <TresMeshStandardMaterial 
        v-else
        :color="project.color"
        :transparent="true"
        :opacity="animatedOpacity"
        :metalness="0.2"
        :roughness="0.4"
      />
    </TresMesh>

    <!-- Card Border -->
    <TresMesh>
      <TresBoxGeometry :args="[2.05, 2.55, 0.05]" />
      <TresMeshBasicMaterial 
        :color="project.color"
        :transparent="true"
        :opacity="animatedOpacity * 0.3"
      />
    </TresMesh>

    <!-- Glow Effect for Active Card -->
    <TresMesh v-if="isActive">
      <TresBoxGeometry :args="[2.3, 2.8, 0.2]" />
      <TresMeshBasicMaterial 
        :color="project.color"
        :transparent="true"
        :opacity="glowOpacity"
      />
    </TresMesh>

    <!-- Image Overlay Plane -->
    <TresMesh v-if="textureLoaded && project.image" :position="[0, 0, 0.06]">
      <TresPlaneGeometry :args="[1.8, 1.2]" />
      <TresMeshBasicMaterial 
        :map="cardTexture"
        :transparent="true"
        :opacity="animatedOpacity * 0.9"
      />
    </TresMesh>
  </TresGroup>
</template>

<script>
import { computed, ref, onMounted, onUnmounted, watch } from 'vue'
import { 
  TresGroup, 
  TresMesh, 
  TresBoxGeometry, 
  TresPlaneGeometry,
  TresMeshStandardMaterial, 
  TresMeshBasicMaterial 
} from '@tresjs/core'
import * as THREE from 'three'
import { gsap } from 'gsap'

export default {
  name: 'ProjectCard3D',
  components: {
    TresGroup,
    TresMesh,
    TresBoxGeometry,
    TresPlaneGeometry,
    TresMeshStandardMaterial,
    TresMeshBasicMaterial
  },
  props: {
    project: {
      type: Object,
      required: true
    },
    index: {
      type: Number,
      required: true
    },
    total: {
      type: Number,
      required: true
    },
    currentIndex: {
      type: Number,
      required: true
    },
    position: {
      type: Array,
      default: () => [0, 0, 0]
    },
    rotation: {
      type: Array,
      default: () => [0, 0, 0]
    },
    scale: {
      type: Array,
      default: () => [1, 1, 1]
    },
    isAnimating: {
      type: Boolean,
      default: false
    }
  },
  emits: ['click'],
  setup(props, { emit }) {
    const cardGroup = ref()
    const cardMesh = ref()
    const cardTexture = ref(null)
    const textureLoaded = ref(false)
    const textureLoader = new THREE.TextureLoader()

    // Animated properties
    const animatedPosition = ref([...props.position])
    const animatedRotation = ref([...props.rotation])
    const animatedScale = ref([...props.scale])
    const animatedOpacity = ref(1)
    const glowOpacity = ref(0)

    // Animation timeline
    let animationTimeline = null

    const isActive = computed(() => props.index === props.currentIndex)

    const opacity = computed(() => {
      const distance = Math.abs(props.index - props.currentIndex)
      return Math.max(0.4, 1 - distance * 0.2)
    })

    // Watch for position changes and animate
    watch(
      () => [props.position, props.rotation, props.scale, props.currentIndex],
      () => {
        animateToPosition()
      },
      { deep: true }
    )

    // Watch for active state changes
    watch(isActive, (newValue) => {
      animateGlow(newValue)
    })

    const animateToPosition = () => {
      if (animationTimeline) {
        animationTimeline.kill()
      }

      animationTimeline = gsap.timeline({
        duration: 0.8,
        ease: "power2.out"
      })

      // Animate position
      animationTimeline.to(animatedPosition.value, {
        0: props.position[0],
        1: props.position[1],
        2: props.position[2],
        duration: 0.8,
        ease: "power2.out"
      }, 0)

      // Animate rotation with slight delay for more natural movement
      animationTimeline.to(animatedRotation.value, {
        0: props.rotation[0],
        1: props.rotation[1],
        2: props.rotation[2],
        duration: 0.6,
        ease: "power2.out"
      }, 0.1)

      // Animate scale
      animationTimeline.to(animatedScale.value, {
        0: props.scale[0],
        1: props.scale[1],
        2: props.scale[2],
        duration: 0.7,
        ease: "back.out(1.2)"
      }, 0)

      // Animate opacity
      animationTimeline.to(animatedOpacity, {
        value: opacity.value,
        duration: 0.5,
        ease: "power2.out"
      }, 0)

      // Add subtle bounce effect for active card
      if (isActive.value) {
        animationTimeline.to(animatedScale.value, {
          0: props.scale[0] * 1.05,
          1: props.scale[1] * 1.05,
          2: props.scale[2] * 1.05,
          duration: 0.2,
          ease: "power2.out",
          yoyo: true,
          repeat: 1
        }, 0.8)
      }
    }

    const animateGlow = (active) => {
      gsap.to(glowOpacity, {
        value: active ? 0.15 : 0,
        duration: 0.4,
        ease: "power2.out"
      })
    }

    // Enhanced click animation
    const handleClick = () => {
      if (cardGroup.value) {
        // Create a quick scale animation on click
        const currentScale = [...animatedScale.value]
        
        gsap.timeline()
          .to(animatedScale.value, {
            0: currentScale[0] * 0.95,
            1: currentScale[1] * 0.95,
            2: currentScale[2] * 0.95,
            duration: 0.1,
            ease: "power2.out"
          })
          .to(animatedScale.value, {
            0: currentScale[0],
            1: currentScale[1],
            2: currentScale[2],
            duration: 0.2,
            ease: "back.out(2)"
          })
      }
      
      emit('click', props.index)
    }

    const loadTexture = () => {
      if (props.project.image) {
        textureLoader.load(
          props.project.image,
          (texture) => {
            texture.wrapS = THREE.ClampToEdgeWrapping
            texture.wrapT = THREE.ClampToEdgeWrapping
            texture.minFilter = THREE.LinearFilter
            texture.magFilter = THREE.LinearFilter
            cardTexture.value = texture
            textureLoaded.value = true
          },
          undefined,
          (error) => {
            console.warn('Failed to load texture:', error)
            textureLoaded.value = false
          }
        )
      }
    }

    onMounted(() => {
      loadTexture()
      // Set initial animated values
      animatedPosition.value = [...props.position]
      animatedRotation.value = [...props.rotation]
      animatedScale.value = [...props.scale]
      animatedOpacity.value = opacity.value
    })

    onUnmounted(() => {
      if (cardTexture.value) {
        cardTexture.value.dispose()
      }
      if (animationTimeline) {
        animationTimeline.kill()
      }
    })

    return {
      cardGroup,
      cardMesh,
      cardTexture,
      textureLoaded,
      isActive,
      opacity,
      animatedPosition,
      animatedRotation,
      animatedScale,
      animatedOpacity,
      glowOpacity,
      handleClick
    }
  }
}
</script>
